import type { ReactNode } from 'react';
import { BookOpen, Gamepad2, GraduationCap, Lock, type LucideIcon } from 'lucide-react';
import { Badge, cn } from '@ludolio/shared-ui';
import { platformFamilies, visibilityLabel } from './format';
import type { LabVisibility } from './types';

export const REVIEW_LIST_COLUMNS = '@2xl:grid-cols-[minmax(0,1fr)_7.5rem_6.5rem_6rem]';
export const PUBLISHED_LIST_COLUMNS = '@2xl:grid-cols-[minmax(0,1fr)_7.5rem_6.5rem_12.5rem]';

export function ProjectList({ columns, headings, children }: { columns: string; headings: string[]; children: ReactNode }) {
  return (
    <div className="@container overflow-hidden rounded-lg border">
      <div
        aria-hidden
        className={cn('hidden gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground @2xl:grid', columns)}
      >
        {headings.map((heading, index) => <span key={index}>{heading}</span>)}
      </div>
      <ul className="divide-y">{children}</ul>
    </div>
  );
}

export function ProjectRow({ columns, children }: { columns: string; children: ReactNode }) {
  return <li className={cn('grid gap-3 p-4 @2xl:items-center', columns)}>{children}</li>;
}

export function RowActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2 *:min-w-24">{children}</div>;
}

export function ProjectHeading({ title, version, iconUrl, children }: {
  title: string;
  version: string;
  iconUrl: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted text-muted-foreground">
        {iconUrl ? <img src={iconUrl} alt="" className="h-full w-full object-cover" /> : <Gamepad2 className="h-5 w-5" aria-hidden />}
      </div>
      <div className="min-w-0 space-y-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium leading-5" title={title}>{title}</span>
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-4 text-muted-foreground">v{version}</span>
        </div>
        {children && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

export function MetaItem({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1" title={label}>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="sr-only">{label}: </span>
      <span className="truncate">{children}</span>
    </span>
  );
}

export function PlatformChips({ platforms }: { platforms: string[] }) {
  const families = platformFamilies(platforms);
  if (families.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      <span className="sr-only">Platforms: </span>
      {families.map((family) => (
        <Badge key={family} variant="outline" className="rounded px-1.5 py-0 text-[11px] font-normal leading-4 tracking-normal">
          {family}
        </Badge>
      ))}
    </div>
  );
}

export function ListCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground">
      <span className="font-medium text-foreground @2xl:hidden">{label}:</span>
      {children}
    </div>
  );
}

const VISIBILITY_ICONS: Record<LabVisibility, LucideIcon> = {
  organization: GraduationCap,
  unit: BookOpen,
  private: Lock,
};

export function VisibilityCell({ visibility }: { visibility: LabVisibility }) {
  const Icon = VISIBILITY_ICONS[visibility];
  return (
    <ListCell label="Who can play">
      <span className="inline-flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {visibilityLabel(visibility)}
      </span>
    </ListCell>
  );
}
